import { Snowflake } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { slateCardClass } from "@/lib/card-styles";
import { typeBodyClass, typeTitleClass } from "@/lib/typography";

/** The cap is a rule, not a setting — three is what a milestone can award. */
const MAX_FREEZES = 3;

interface StreakFreezeStatusProps {
  freezesAvailable: number;
  freezesUsed: number;
  currentStreak: number;
  className?: string;
  onFreezeUsed?: (message: string) => void;
}

/**
 * How many streak freezes are in hand, and what they do.
 *
 * Three slots rather than a count: the cap is three, so three filled/empty
 * slots say "2 of 3" without a fraction, and the empty ones are visibly spare
 * capacity rather than an absence. `currentStreak` is accepted but unused — a
 * freeze only ever applies to a missed day, so there is nothing on this screen
 * that the streak count would change.
 */
export const StreakFreezeStatus = ({
  freezesAvailable,
  freezesUsed,
  className,
}: StreakFreezeStatusProps) => {
  const { t } = useTranslation();

  return (
    <div className={cn("space-y-4", className)}>
      <div>
        <h3 className={typeTitleClass}>
          {t("gamification.streak.freezesTitle", "Streak freezes")}
        </h3>
        <p className={cn(typeBodyClass, "mt-1")}>
          {t("gamification.streak.freezesUsed", {
            defaultValue: "{{count}} used so far",
            count: freezesUsed,
          })}
        </p>
      </div>

      <div
        className={cn(slateCardClass, "flex items-center justify-center gap-3 px-4 py-5")}
        role="img"
        aria-label={t("gamification.streak.freezesAvailable", {
          defaultValue: "{{count}} of {{max}} freezes available",
          count: freezesAvailable,
          max: MAX_FREEZES,
        })}
      >
        {Array.from({ length: MAX_FREEZES }).map((_, i) => {
          const available = i < freezesAvailable;
          return (
            <span
              key={i}
              className={cn(
                "flex h-12 w-12 items-center justify-center rounded-xl border transition-colors",
                available
                  ? "border-cyan-200 bg-cyan-50 text-cyan-600 dark:border-cyan-900 dark:bg-cyan-950/40 dark:text-cyan-300"
                  : "border-dashed border-stone-200 text-stone-300 dark:border-stone-800 dark:text-stone-700",
              )}
            >
              <Snowflake className="h-5 w-5" aria-hidden="true" />
            </span>
          );
        })}
      </div>

      <p className={typeBodyClass}>
        {t(
          "gamification.streak.freezeExplanation",
          "A freeze is spent automatically the day you miss, so the streak survives. Earn them from milestones.",
        )}
      </p>
    </div>
  );
};

export default StreakFreezeStatus;